/**
 * WW Aurora Field — the live colour field behind the Plaud Intelligence opener
 * Author: Plaud Tayler — 2026-09-08
 * Page: Figma node 1227:14097 "Hero Section" (Product page — Plaud Intelligence)
 * Version: 1.2.0 - 2026-09-08 23:05 (UTC+8) - Plaud Tayler — renamed off the hero: the field is the shared
 *   canvas behind both halves of the opener, so it is no longer named after one of them. Behaviour unchanged;
 *   was assets/ww-intelligence-hero.js, element <ww-intelligence-hero>.
 * Version-note: 1.1.0 - 2026-09-08 21:40 (UTC+8) - Plaud Tayler — the pointer's reach was the prototype's,
 *   which was tuned on a 1717-tall stage where the cursor mostly sat among the blobs. In an 862 hero the
 *   cursor spends its time above them, so the same falloff left almost nothing moving: reach, push and
 *   the thinning that makes the void legible are all widened, and the engage ramp is roughly twice as
 *   quick so the field answers the first gesture rather than the third. All four are tokens.
 * Version-note: 1.0.0 - 2026-09-08 20:10 (UTC+8) - Plaud Tayler — orbiting blob field, pointer repulsion
 *   and breathing, ported from the signed-off motion prototype (Hero_gradient.html)
 *
 * What this owns and what it deliberately does not:
 *   - It does NOT own the aurora that ships in the HTML. The still JPEG is the LCP art, it renders
 *     with no JS, under reduced motion, and for the ~1s before this file has measured anything. The
 *     field only takes over once it is running, via the .is-aurora-live class, and hands back on
 *     unload. That ordering is the whole reason the section can carry motion without touching LCP.
 *   - It writes transforms and two custom properties. Every size, colour and alpha lives in
 *     ww-intelligence-hero.css as a token; nothing here hardcodes an appearance.
 *
 * Geometry. Three rings of blobs revolve around the product centre at different radii and opposite
 * signs, so the field never settles into a readable rotation. All figures are fractions of the
 * FIELD WIDTH (design px / 1440), which is what keeps the composition intact away from 1440 —
 * the same reasoning the stylesheet's percentage margins follow.
 *
 * Rule 6:
 *   1. Paused off-viewport — an IntersectionObserver stops the loop, and visibilitychange stops it
 *      in a background tab. A hero is on screen at load and off it for the rest of the visit.
 *   2. prefers-reduced-motion — the loop never starts and .is-aurora-live is never added, so the
 *      still artwork is what renders. The media query is watched, so toggling it at runtime is
 *      honoured without a reload.
 *   3. Cross-browser — transform and radial-gradient only, no filter and no blend on the moving
 *      layers, so there is nothing here that Safari or Firefox rasterises differently.
 */
if (!customElements.get('ww-aurora-field')) {
  customElements.define(
    'ww-aurora-field',
    class WwAuroraField extends HTMLElement {
      /* Fractions of the field width, so the effect scales with the viewport the same way the
         geometry does. The prototype used 0.361 / 0.104 / 0.30 / 0.13 on a stage where the cursor
         moved through the blobs; over an 862 hero the cursor is usually above them, which is why
         these are wider. ENGAGE is the per-frame approach rate of the effect's own 0..1 envelope. */
      static REACH = 0.62;
      static MAX_PUSH = 0.2;
      static STRENGTH = 0.42;
      static THIN = 0.22;
      static ENGAGE = 0.12;

      constructor() {
        super();
        this.root = null;
        this.field = null;
        this.rings = [];
        this.raf = 0;
        this.io = null;
        this.mql = null;
        this.inView = false;
        this.unit = 0;
        this.pivotY = 0;
        this.speed = 1;
        this.pointerEnabled = false;

        /* pointer: raw, eased, and how much of the effect is currently dialled in */
        this.rawX = 0;
        this.rawY = 0;
        this.px = 0;
        this.py = 0;
        this.attract = 0;
        this.attractTarget = 0;
        this.boost = 0;

        this.last = 0;
        this.time = 0;

        this.onFrame = this.frame.bind(this);
        this.onResize = this.measure.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerLeave = this.handlePointerLeave.bind(this);
        this.onVisibility = this.handleVisibility.bind(this);
        this.onMotionChange = this.handleMotionChange.bind(this);
      }

      connectedCallback() {
        this.root = this.querySelector('.ww-ih');
        this.field = this.querySelector('.ww-ih__field');
        if (!this.root || !this.field) return;

        this.speed = parseFloat(this.dataset.auroraSpeed || '1');
        if (!isFinite(this.speed) || this.speed <= 0) this.speed = 1;

        /* One knob scales how far the cursor reaches and how hard it pushes together, because
           moving either alone changes the shape of the effect rather than its amount. */
        this.power = parseFloat(this.dataset.auroraPointerPower || '1');
        if (!isFinite(this.power) || this.power <= 0) this.power = 1;

        /* [element, radius fraction, deg/sec, angle] — two rings turn one way, the middle one the
           other, so the pattern never repeats on a short cycle. */
        var els = this.querySelectorAll('.ww-ih__ring');
        if (els.length < 3) return;
        this.rings = [
          { el: els[0], r: 330 / 1440, speed: 5.6, angle: (200 * Math.PI) / 180 },
          { el: els[1], r: 560 / 1440, speed: -3.8, angle: (300 * Math.PI) / 180 },
          { el: els[2], r: 770 / 1440, speed: 7.2, angle: (60 * Math.PI) / 180 }
        ];

        for (var i = 0; i < this.rings.length; i++) {
          var kids = this.rings[i].el.children;
          for (var j = 0; j < kids.length; j++) {
            /* base angle on its own ring, plus a breathing phase that is not shared with a sibling */
            kids[j].__a = (j / kids.length) * Math.PI * 2;
            kids[j].__ph = j * 1.7 + this.rings[i].r * 14.4;
          }
        }

        this.mql = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (this.mql.addEventListener) this.mql.addEventListener('change', this.onMotionChange);
        else if (this.mql.addListener) this.mql.addListener(this.onMotionChange);

        this.pointerEnabled =
          this.dataset.auroraPointer !== '0' &&
          window.matchMedia('(hover: hover) and (pointer: fine)').matches;

        this.measure();
        this.observe();
        window.addEventListener('resize', this.onResize);
        document.addEventListener('visibilitychange', this.onVisibility);
      }

      disconnectedCallback() {
        this.stop();
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.pointerEnabled && this.root) {
          this.root.removeEventListener('pointermove', this.onPointerMove);
          this.root.removeEventListener('pointerleave', this.onPointerLeave);
        }
        if (this.io) { this.io.disconnect(); this.io = null; }
        if (this.mql) {
          if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.onMotionChange);
          else if (this.mql.removeListener) this.mql.removeListener(this.onMotionChange);
        }
        if (this.root) this.root.classList.remove('is-aurora-live');
      }

      get reduced() {
        return !!(this.mql && this.mql.matches);
      }

      /* Field width is the single unit everything derives from, so one measurement covers the ring
         radii, the blob anchors and the pointer coordinates. */
      measure() {
        if (!this.field) return;
        var w = this.field.clientWidth;
        if (!w) return;
        this.unit = w;
        /* Every blob is sized off this, because a percentage inside a zero-sized ring collapses. */
        this.field.style.setProperty('--wih-u', w + 'px');
        /* The rings carry the pivot as a width-relative margin, so reading the first ring's own
           offset is the same number the stylesheet computed — no need to restate the fraction. */
        var firstRing = this.rings.length ? this.rings[0].el : null;
        this.pivotY = firstRing ? firstRing.offsetTop : w * 0.54583;

        for (var i = 0; i < this.rings.length; i++) {
          var ring = this.rings[i];
          var radius = ring.r * w;
          var kids = ring.el.children;
          for (var j = 0; j < kids.length; j++) {
            kids[j].style.left = (Math.cos(kids[j].__a) * radius).toFixed(1) + 'px';
            kids[j].style.top = (Math.sin(kids[j].__a) * radius).toFixed(1) + 'px';
          }
        }

        if (!this.px) { this.px = this.rawX = w / 2; this.py = this.rawY = this.pivotY; }
      }

      observe() {
        if (typeof IntersectionObserver !== 'function') { this.inView = true; this.start(); return; }
        this.io = new IntersectionObserver(
          (entries) => {
            var visible = entries.some(function (e) { return e.isIntersecting; });
            if (visible === this.inView) return;
            this.inView = visible;
            if (visible) this.start(); else this.stop();
          },
          { rootMargin: '120px 0px' }
        );
        this.io.observe(this);
      }

      start() {
        if (this.raf || this.reduced || !this.inView || document.hidden || !this.unit) return;
        if (this.pointerEnabled) {
          this.root.addEventListener('pointermove', this.onPointerMove);
          this.root.addEventListener('pointerleave', this.onPointerLeave);
        }
        this.root.classList.add('is-aurora-live');
        this.last = performance.now();
        this.raf = requestAnimationFrame(this.onFrame);
      }

      stop() {
        if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
        if (this.pointerEnabled && this.root) {
          this.root.removeEventListener('pointermove', this.onPointerMove);
          this.root.removeEventListener('pointerleave', this.onPointerLeave);
        }
      }

      handleVisibility() {
        if (document.hidden) this.stop(); else this.start();
      }

      handleMotionChange() {
        if (this.reduced) {
          this.stop();
          if (this.root) this.root.classList.remove('is-aurora-live');
        } else {
          this.start();
        }
      }

      handlePointerMove(e) {
        var r = this.field.getBoundingClientRect();
        var x = e.clientX - r.left;
        var y = e.clientY - r.top;
        var dx = x - this.rawX;
        var dy = y - this.rawY;
        /* a fast sweep briefly speeds the rings up, so the field answers the gesture rather than
           only the position */
        this.boost = Math.min(1.1, this.boost + Math.sqrt(dx * dx + dy * dy) * 0.012);
        this.rawX = x;
        this.rawY = y;
        this.attractTarget = 1;
      }

      handlePointerLeave() {
        this.attractTarget = 0;
      }

      frame(now) {
        this.raf = requestAnimationFrame(this.onFrame);

        var dt = Math.min(0.05, (now - this.last) / 1000);
        this.last = now;
        this.time += dt;

        this.px += (this.rawX - this.px) * 0.16;
        this.py += (this.rawY - this.py) * 0.16;
        this.attract += (this.attractTarget - this.attract) * WwAuroraField.ENGAGE;
        this.boost *= Math.pow(0.22, dt);

        if (this.pointerEnabled) {
          this.field.style.setProperty('--wih-px', this.px.toFixed(1) + 'px');
          this.field.style.setProperty('--wih-py', this.py.toFixed(1) + 'px');
          this.field.style.setProperty('--wih-po', this.attract.toFixed(3));
        }

        /* Repulsion, not attraction: the pointer clears a soft void instead of dragging colour
           behind it. Peak displacement of this field is 0.5 * STRENGTH * falloff, reached where the
           blob sits one falloff radius away — so widening the reach is what grows both the area
           that responds and how far the nearest blobs travel. */
        var maxPush = this.unit * WwAuroraField.MAX_PUSH * this.power;
        var falloff = this.unit * WwAuroraField.REACH * this.power;
        var strength = WwAuroraField.STRENGTH;
        var cx = this.unit / 2;
        var cy = this.pivotY;
        var k = 1 + this.boost;

        for (var i = 0; i < this.rings.length; i++) {
          var ring = this.rings[i];
          ring.angle += ((ring.speed * Math.PI) / 180) * dt * k * this.speed;
          var deg = (ring.angle * 180) / Math.PI;
          ring.el.style.transform = 'rotate(' + deg.toFixed(3) + 'deg)';

          var radius = ring.r * this.unit;
          var kids = ring.el.children;
          for (var j = 0; j < kids.length; j++) {
            var el = kids[j];
            var a = el.__a + ring.angle;
            var wx = cx + Math.cos(a) * radius;
            var wy = cy + Math.sin(a) * radius;
            var dx = wx - this.px;
            var dy = wy - this.py;
            var d = Math.sqrt(dx * dx + dy * dy) || 1;
            var f = 1 / (1 + (d / falloff) * (d / falloff));
            var push = strength * f * this.attract;
            var tx = Math.max(-maxPush, Math.min(maxPush, dx * push));
            var ty = Math.max(-maxPush, Math.min(maxPush, dy * push));
            /* thin it as it is displaced, so the void reads as cleared rather than covered */
            var sc = 1 + 0.13 * Math.sin(this.time * 0.28 + el.__ph)
                       - WwAuroraField.THIN * f * this.attract;
            /* counter-rotate first: the push has to be applied in field space, not ring space */
            el.style.transform =
              'rotate(' + (-deg).toFixed(3) + 'deg) translate(' +
              tx.toFixed(1) + 'px,' + ty.toFixed(1) + 'px) scale(' + sc.toFixed(3) + ')';
          }
        }
      }
    }
  );
}
